import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { isPossiblePhoneNumber } from "react-phone-number-input";

import { T, photoAngles } from "../../constants";
import { useUi, cx } from "../../ui";
import ModalCard from "../../components/ModalCard";
import FieldRow from "../../components/FieldRow";
import PhoneField from "../../components/PhoneField";
import Plate from "../../components/Plate";
import VipBadge from "../../components/VipBadge";
import PhotoTile from "./PhotoTile";
import CameraCaptureModal from "./CameraCaptureModal";
import { useInstallation } from "../../../../components/InstallationContext";
import authManager from "../../../../lib/authManager";
import { VEHICLE_PHOTO_MODES } from "../../../../lib/conciergeFeature";

const useStyles = makeStyles({
  steps: { display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 14 },
  step: {
    fontSize: 11,
    padding: "3px 8px",
    borderRadius: 999,
    background: T.surface,
    color: T.mutedFg,
    border: `1px solid ${T.borderSoft}`,
  },
  stepCurrent: {
    background: T.accent,
    color: T.accentFg,
    borderColor: T.primary,
    fontWeight: 600,
  },
  stepDone: { color: T.fg },
  head: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  quiet: { fontSize: 12, color: T.mutedFg, marginTop: 10 },
  small: { padding: "8px 12px", fontSize: 13 },
  meta: { fontSize: 12, color: T.mutedFg },
  photoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    marginTop: 4,
  },
  photoCount: { fontSize: 12, color: T.mutedFg, marginBottom: 6 },
  received: { fontSize: 14, fontWeight: 600, color: T.fg, padding: "6px 0" },
});

const STEP_LABEL = { MOBILE: "Mobile", PHOTOS: "Photos", KEYS: "Keys" };

/*
 * Taking a car in, one answer per screen.
 *
 * The three steps are the three things the desk physically does: get a number
 * to send the pass to, walk the car, take the keys. Everything the old detected
 * card carried that is not one of those has moved off this path.
 */
export default function IntakeStepper({
  booking,
  photos,
  onCapture,
  onRetryPhoto,
  allPhotos,
  uploadingPhotos,
  hydratingPhotos,
  vehicleInput,
  onVehicleInputChange,
  onSubmitPhone,
  savingPhone,
  onFinish,
  claiming,
  onClose,
  vehiclePhotoMode = VEHICLE_PHOTO_MODES.OPTIONAL,
}) {
  const s = useStyles();
  const ui = useUi();
  const { facility } = useInstallation() ?? {};

  const [index, setIndex] = useState(0);
  /* Prefilled from the booking: most guests are already on file, and retyping
   * a number that is already right is how a wrong one gets saved. */
  const [phone, setPhone] = useState(booking.phone ?? "");
  const [openAngle, setOpenAngle] = useState(null);
  const [photosWaived, setPhotosWaived] = useState(false);

  const photosDisabled = vehiclePhotoMode === VEHICLE_PHOTO_MODES.DISABLED;
  const photosRequired = vehiclePhotoMode === VEHICLE_PHOTO_MODES.REQUIRED;
  const steps = photosDisabled
    ? ["MOBILE", "KEYS"]
    : ["MOBILE", "PHOTOS", "KEYS"];
  const step = steps[index];

  useEffect(() => {
    if (photosDisabled) {
      setIndex((current) => Math.min(current, 1));
    }
  }, [photosDisabled]);

  const open = photoAngles.find((a) => a.angle === openAngle);
  const doneCount = photoAngles.filter(
    ({ angle }) => photos[angle]?.status === "done",
  ).length;

  const phoneValid = Boolean(phone) && isPossiblePhoneNumber(phone);
  const phoneChanged = phone !== (booking.phone ?? "");

  const receivedBy = authManager.userName ?? "You";

  const next = () => setIndex((current) => Math.min(current + 1, steps.length - 1));
  const back = () => setIndex((current) => Math.max(current - 1, 0));

  const confirmPhone = async () => {
    if (!phoneValid || savingPhone) return;

    if (phoneChanged) {
      const saved = await onSubmitPhone(phone);
      if (!saved) return;
    }

    next();
  };

  const primary = {
    MOBILE: (
      <button
        type="button"
        className={cx(ui.primaryBtn, s.small)}
        disabled={!phoneValid || savingPhone}
        onClick={confirmPhone}
      >
        {savingPhone ? "Saving…" : "Confirm mobile number"}
      </button>
    ),
    PHOTOS: (
      <>
        {!photosRequired && !allPhotos && !photosWaived && !hydratingPhotos && (
          <button
            type="button"
            className={cx(ui.mutedBtn, s.small)}
            disabled={uploadingPhotos}
            onClick={() => {
              setPhotosWaived(true);
              next();
            }}
          >
            Continue without photos
          </button>
        )}
        <button
          type="button"
          className={cx(ui.primaryBtn, s.small)}
          disabled={(!allPhotos && !photosWaived) || uploadingPhotos}
          onClick={next}
        >
          {uploadingPhotos ? "Uploading photos…" : "Continue"}
        </button>
      </>
    ),
    KEYS: (
      <button
        type="button"
        className={cx(ui.primaryBtn, s.small)}
        disabled={claiming}
        onClick={onFinish}
      >
        {claiming ? "Starting booking…" : "Keys received · finish intake"}
      </button>
    ),
  }[step];

  return (
    <ModalCard
      title={`Intake · ${STEP_LABEL[step]} · step ${index + 1} of ${steps.length}`}
      onClose={onClose}
      width={560}
      z={1350}
      footerLeft={
        <button
          type="button"
          className={cx(ui.mutedBtn, s.small)}
          onClick={index === 0 ? onClose : back}
        >
          Back
        </button>
      }
      footerRight={primary}
    >
      <div className={s.steps} aria-label="Intake steps">
        {steps.map((item, position) => (
          <span
            key={item}
            className={cx(
              s.step,
              position === index && s.stepCurrent,
              position < index && s.stepDone,
            )}
            aria-current={position === index ? "step" : undefined}
          >
            {position + 1}. {STEP_LABEL[item]}
          </span>
        ))}
      </div>

      <div className={s.head}>
        <Plate size={14}>{booking.plate}</Plate>
        <strong>{booking.guestName}</strong>
        {booking.isVip && <VipBadge />}
      </div>

      {step === "MOBILE" && (
        <FieldRow
          label="Guest mobile"
          hint="Required. The valet pass and payment link are sent to this number."
        >
          <PhoneField
            value={phone}
            onChange={(next) => setPhone(next ?? "")}
            defaultCountry={
              typeof facility?.city?.country === "string"
                ? facility.city.country.toUpperCase()
                : undefined
            }
            placeholder="4XX XXX XXX"
            invalid={Boolean(phone) && !phoneValid}
          />
          {Boolean(phone) && !phoneValid && (
            <span className={s.meta}>
              Enter a mobile number this guest can be reached on.
            </span>
          )}
        </FieldRow>
      )}

      {step === "PHOTOS" && (
        <>
          <FieldRow
            label="Vehicle (optional)"
            htmlFor="intake-vehicle"
            hint="Appears on the dashboard so the runner can find the car."
          >
            <input
              id="intake-vehicle"
              className={ui.field}
              value={vehicleInput}
              onChange={(e) => onVehicleInputChange(e.target.value)}
              placeholder="Make, model, colour"
            />
          </FieldRow>

          <div className={s.photoCount}>
            {hydratingPhotos
              ? "Loading any photos already taken for this booking…"
              : `${doneCount}/${photoAngles.length} captured`}
          </div>
          <div className={s.photoGrid}>
            {photoAngles.map(({ angle, label }) => {
              const state = photos[angle] ?? {};

              return (
                <PhotoTile
                  key={angle}
                  label={label}
                  imageUrl={state.imageUrl}
                  status={state.status}
                  progress={state.progress}
                  error={state.error}
                  onCapture={() => setOpenAngle(angle)}
                  onRetry={() => onRetryPhoto(angle)}
                />
              );
            })}
          </div>
          <div className={s.quiet}>
            Photos protect the hotel against damage disputes.
          </div>
        </>
      )}

      {step === "KEYS" && (
        <>
          <FieldRow
            label="Vehicle and keys received by"
            hint="Recorded against your account; the keys are yours until a runner takes them."
          >
            <div className={s.received}>{receivedBy}</div>
          </FieldRow>

          {booking.paymentRequired !== false && booking.paid === false && (
            <div className={s.quiet} role="note">
              Outstanding balance · paid by card or payment link before pickup.
            </div>
          )}

          {booking.isVip && (
            <div className={s.quiet} role="note">
              VIP arrival · the booking's contacts are paged when intake
              completes.
            </div>
          )}
        </>
      )}

      {open && (
        <CameraCaptureModal
          label={open.label}
          onCancel={() => setOpenAngle(null)}
          onConfirm={(blob, dims) => {
            setOpenAngle(null);
            onCapture(open.angle, blob, dims);
          }}
        />
      )}
    </ModalCard>
  );
}
